import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { GRUENDE, BEGLEITBEFUNDE, SKALEN, ZUSTIMMUNG } from '../config/fragebogenLabels';

// Farben wie im Auswertungsbericht (eine Blau-Rampe, neutrale Grautöne)
const BLAU = '#2a78d6';
const SPUR = '#e1e0d9';
const LEISE = '#6b6a66';

const datum = (wert) => {
  if (!wert) return null;
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? null : d;
};
const alsDatum = (wert) => datum(wert)?.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) || '–';
const monateZwischen = (von, bis) => (von && bis && bis > von ? Math.round((bis - von) / (1000 * 60 * 60 * 24 * 30.4375)) : null);

const Abschnitt = ({ titel, hinweis, children }) => (
  <Box sx={{ mb: 3, breakInside: 'avoid' }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, borderBottom: `1px solid ${SPUR}`, pb: 0.5, mb: 1.5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 16 }}>{titel}</Typography>
      {hinweis && <Typography sx={{ fontSize: 12.5, color: LEISE }}>{hinweis}</Typography>}
    </Box>
    {children}
  </Box>
);

const Feld = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: 12, color: LEISE, lineHeight: 1.3 }}>{label}</Typography>
    <Typography sx={{ fontSize: 15 }}>{children || '–'}</Typography>
  </Box>
);

// Skala als Segmentleiste: gefüllt bis zum gewählten Wert
const Leiste = ({ wert, stufen }) => (
  <Box sx={{ display: 'flex', gap: '3px' }} aria-hidden>
    {Array.from({ length: stufen }, (_, i) => (
      <Box key={i} sx={{ width: 26, height: 10, borderRadius: '2px', bgcolor: i < wert ? BLAU : SPUR }} />
    ))}
  </Box>
);

const Zeile = ({ label, wert, stufen, text }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,1fr) 160px 150px' }, alignItems: 'center', columnGap: 2, rowGap: 0.5, py: 0.9, borderBottom: `1px solid ${SPUR}` }}>
    <Typography sx={{ fontSize: 15 }}>{label}</Typography>
    <Leiste wert={wert} stufen={stufen} />
    <Typography sx={{ fontSize: 14, color: wert ? 'text.primary' : LEISE }}>
      {wert ? <><b>{wert}</b> · {text}</> : text}
    </Typography>
  </Box>
);

/**
 * Einzelanzeige eines ausgefüllten Bogens (Admin-Dialog, Druck und PDF nutzen denselben Inhalt).
 */
const OSPBogenAnsicht = ({ data, patient, session, submittedAt }) => {
  const pd = data.personendaten || {};
  const name = pd.name || patient?.name || `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim() || 'Unbekannt';
  const geschlecht = { m: 'Männlich', w: 'Weiblich', M: 'Männlich', F: 'Weiblich' }[pd.geschlecht || patient?.gender];

  const eingereicht = datum(submittedAt);
  const geburt = datum(patient?.date_of_birth);
  const alter = eingereicht && geburt && geburt.getFullYear() > 1900
    ? Math.floor((eingereicht - geburt) / (1000 * 60 * 60 * 24 * 365.25)) : null;

  const von = datum(pd.tragezeitVon);
  const bis = datum(pd.tragezeitBis);
  const dauer = monateZwischen(von, bis && eingereicht && bis > eingereicht ? eingereicht : bis || eingereicht);

  // Verbesserungen sind unter dem Anzeigetext gespeichert; ältere Bögen nutzen die Langform
  const bewertungen = {};
  Object.entries(data.verbesserungen || {}).forEach(([label, wert]) => {
    const l = String(label).toLowerCase();
    const treffer = GRUENDE.find(([, text]) => text.toLowerCase() === l)
      || (l.startsWith('zahn') ? GRUENDE.find(([key]) => key === 'zahnKieferfehlstellungen') : null);
    bewertungen[treffer ? treffer[0] : label] = Number(wert) || 0;
  });
  const gruende = GRUENDE.filter(([key]) => data.gruende?.[key]);
  const bewertet = gruende.map(([key]) => bewertungen[key]).filter((w) => w >= 1);
  const mittel = bewertet.length ? (bewertet.reduce((a, b) => a + b, 0) / bewertet.length).toFixed(1).replace('.', ',') : null;

  return (
    <Box sx={{ color: '#0b0b0b' }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 12.5, color: LEISE }}>OSP-Fragebogen · ISST Unna</Typography>
        <Typography sx={{ fontSize: 24, fontWeight: 650, lineHeight: 1.2 }}>{name}</Typography>
        <Typography sx={{ fontSize: 13.5, color: LEISE, mt: 0.5 }}>
          Eingereicht am {alsDatum(submittedAt)}
          {patient?.patient_id ? ` · Patienten-ID ${patient.patient_id}` : ''}
          {patient?.email ? ` · ${patient.email}` : ''}
          {session?.session_id ? ` · Bogen ${String(session.session_id).slice(0, 8)}` : ''}
        </Typography>
      </Box>

      <Abschnitt titel="Angaben zum Kind">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
          <Feld label="Geschlecht">{geschlecht}</Feld>
          <Feld label="Geburtsdatum">{geburt ? alsDatum(patient.date_of_birth) : null}</Feld>
          <Feld label="Alter bei Befragung">{alter !== null ? `${alter} Jahre` : null}</Feld>
          <Feld label="Platten">
            {pd.anzahlPlatten ? `${pd.anzahlPlatten} eingesetzt` : null}
            {pd.plattenNummer ? ` · aktuell die ${pd.plattenNummer}.` : ''}
          </Feld>
          <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 2' } }}>
            <Feld label="Diagnose">{pd.diagnose}</Feld>
          </Box>
          <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 2' } }}>
            <Feld label="Tragezeit">
              {von || bis ? `${von ? alsDatum(pd.tragezeitVon) : '–'} bis ${bis ? alsDatum(pd.tragezeitBis) : 'heute'}` : null}
              {dauer ? ` · ${dauer} Monate` : ''}
            </Feld>
          </Box>
        </Box>
      </Abschnitt>

      <Abschnitt
        titel="Gründe für die OSP und Verbesserung"
        hinweis={mittel ? `Mittlere Bewertung ${mittel} von 5` : null}
      >
        {gruende.length === 0 ? (
          <Typography sx={{ color: LEISE }}>Keine Gründe angegeben.</Typography>
        ) : (
          gruende.map(([key, label]) => {
            const wert = bewertungen[key] >= 1 ? bewertungen[key] : 0;
            return <Zeile key={key} label={label} wert={wert} stufen={5} text={wert ? ZUSTIMMUNG[wert] : 'nicht bewertet'} />;
          })
        )}
        <Typography sx={{ fontSize: 12, color: LEISE, mt: 1 }}>
          Bewertet wird die Aussage „Dieser Bereich hat sich durch das Tragen der OSP verbessert“ (1 = stimme gar nicht zu, 5 = stimme voll zu).
        </Typography>
      </Abschnitt>

      <Abschnitt titel="Gesamturteile">
        {SKALEN.map(([feld, titel, stufen]) => {
          const roh = pd[feld] || data[feld];
          const index = stufen.findIndex(([key]) => key === roh);
          return <Zeile key={feld} label={titel} wert={index + 1} stufen={4} text={index >= 0 ? stufen[index][1] : 'keine Angabe'} />;
        })}
      </Abschnitt>

      <Abschnitt titel="Begleitbefunde">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 4, rowGap: 2 }}>
          {BEGLEITBEFUNDE.map(([block, titel, felder]) => {
            const gewaehlt = felder.filter(([key]) => data[block]?.[key]);
            return (
              <Box key={block}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>{titel}</Typography>
                {gewaehlt.length === 0 ? (
                  <Typography sx={{ fontSize: 14, color: LEISE }}>nichts angekreuzt</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {gewaehlt.map(([key, label]) => (
                      <Chip key={key} label={label} size="small" variant="outlined" sx={{ height: 'auto', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        <Typography sx={{ fontSize: 12, color: LEISE, mt: 1.5 }}>OK = Oberkiefer, UK = Unterkiefer</Typography>
      </Abschnitt>
    </Box>
  );
};

export default OSPBogenAnsicht;
